import Vue from "vue";
import Vuex from "vuex";
import moment from "moment-timezone";
import Api from "@/services/Api.service.js";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    loaderOpen: false,
    flags: {
      flightReview: false,
      isDomesticReturn: false,
      searchInProgress: false,
      allSelected: false,
    },
    urlSearchParameters: undefined,
    searchParameters: {},
    searchResults: {
      outbound: [],
      return: [],
      errors: [],
    },
    priorSearch: {
      exists: false,
      arrivalLocation: {
        airportName: undefined,
        airportCode: undefined,
      },
      departureLocation: {
        airportName: undefined,
        airportCode: undefined,
      },
      departureTime: "AnyTime",
      adults: 1,
      children: 0,
      infants: 0,
      preferredCabin: "Economy",
      departureDate: undefined,
      returnDate: undefined,
      multiFlights: [],
      tripType: "return",
    },
    resultConstants: {
      marketingAirlines: [],
      minOutboundTime: 0,
      maxOutboundTime: 0,
      minReturnTime: 0,
      maxReturnTime: 0,
      minPrice: 0,
      maxPrice: 0,
      minTravelTime: 0,
      maxTravelTime: 0,
    },
    filteredOutbound: [],
    outbound: [],
    return: [],
    filteredReturn: [],
    selectedFlights: {
      outbound: {},
      return: {},
    },
    payer: {},
    passengers: {
      adults: [],
      children: [],
      infants: [],
    },
    allFlights: undefined,
    departureTimezone: undefined,
    arrivalTimezone: undefined,
    extraDetails: {},
    countries: [],
    priceResponse: {},
    products: [],
    baggageCost: 0,
    paymentCharge: 0,
    paymentMethodResponse: {},
    paymentOptions: [],
    cardDetails: undefined,
    bookingResponse: {},
    validationErrors: [],
    pnpReferenceNumber: undefined,
    currentBlock: 1,
    session: generateRandomID(4),
    bookBody: {},
    authBookingResult: {},
    isInternational: false,
  },
  mutations: {
    setIsInternational(state, isInternational) {
      state.isInternational = isInternational;
    },
    saveBookBody(state, body) {
      state.bookBody = body;
    },
    recordSearch(state, search) {
      state.priorSearch = search;
    },
    setLoader(state, bool) {
      state.loaderOpen = bool;
    },
    setPnpReferenceNumber(state, ref) {
      state.pnpReferenceNumber = ref;
    },
    setAuthBookingResult(state, res) {
      state.authBookingResult = res;
    },
    setAllFlights(state, flights) {
      state.allFlights = flights;
    },
    setUrlSearchParameters(state, params) {
      state.urlSearchParameters = params;
    },
    updateBlock(state, block) {
      state.currentBlock = block;
    },
    resetSession(state) {
      state.session = generateRandomID(4);
    },
    updatePaymentCharge(state, cost) {
      state.paymentCharge = cost;
    },
    setCountries(state, countries) {
      state.countries = countries.data;
    },
    toggleFlightReview(state) {
      state.flags.flightReview = !state.flags.flightReview;
    },
    setSearchParameters(state, searchParameters) {
      state.searchParameters = searchParameters;
    },
    setPaymentMethodResponse(state, paymentMethods) {
      state.paymentMethodResponse = paymentMethods;
      state.paymentOptions = paymentMethods.paymentOptions;
    },
    setExtraDetails(state, extraDeatils) {
      state.extraDetails = extraDeatils;
    },
    setCardDetails(state, details) {
      state.cardDetails = details;
    },
    setBookingResponse(state, response) {
      state.bookingResponse = response;
      if (response.validationResults) {
        if (
          response.validationResults.passengersValidationResult &&
          response.validationResults.passengersValidationResult.invalidFields
        ) {
          state.validationErrors.concat(
            Object.keys(
              response.validationResults.passengersValidationResults
                .invalidFields
            )
          );
        }
        if (
          response.validationResults.contactDetailsValidationResults &&
          response.validationResults.contactDetailsValidationResults
            .invalidFields
        ) {
          state.validationErrors.concat(
            Object.keys(
              response.validationResults.contactDetailsValidationResults
                .invalidFields
            )
          );
        }
        if (
          response.validationResults.productsDetailsValidationResults &&
          response.validationResults.productsDetailsValidationResults
            .invalidFields
        ) {
          state.validationErrors.concat(
            Object.keys(
              response.validationResults.productsDetailsValidationResults
                .invalidFields
            )
          );
        }
        if (
          response.validationResults.paymentDetailsValidationResults &&
          response.validationResults.paymentDetailsValidationResults
            .invalidFields
        ) {
          state.validationErrors.concat(
            Object.keys(
              response.validationResults.paymentDetailsValidationResults
                .invalidFields
            )
          );
        }
      }
    },
    setSearchResults(state, searchResults) {
      if (searchResults.errors !== null) {
        state.searchResults.errors = searchResults.errors;
      } else {
        let outbound = searchResults.isBundled
          ? searchResults.itineraries
          : searchResults.outboundItineraries;
        state.flags.isDomesticReturn = searchResults.inboundItineraries
          ? true
          : false;
        for (let i = 0; i < outbound.length; ++i) {
          outbound[i].key = generateRandomID(3);
          outbound[i].marketingAirlines = [];
          outbound[i].biggestTravelTime = 0;
          outbound[i].absoluteDepartureTime = moment(
            outbound[i].odoList[0].segments[0].departureDateTime
          ).format("HH:mm");
          outbound[i].formattedDepartureDate = moment(
            outbound[i].odoList[0].segments[0].departureDateTime
          ).format("dddd, MMMM Do YYYY");
          outbound[i].absoluteArrivalTime = moment(
            outbound[i].odoList[outbound[i].odoList.length - 1].segments[
              outbound[i].odoList[outbound[i].odoList.length - 1].segments
                .length - 1
            ].arrivalDateTime
          ).format("HH:mm");
          // find the price Range
          let currentNumber = Number(outbound[i].amount);
          if (state.resultConstants.minPrice == 0) {
            state.resultConstants.minPrice = currentNumber;
            state.resultConstants.maxPrice = currentNumber;
          } else if (currentNumber < state.resultConstants.minPrice) {
            state.resultConstants.minPrice = currentNumber;
          } else if (currentNumber > state.resultConstants.maxPrice) {
            state.resultConstants.maxPrice = currentNumber;
          }
          for (let leg of outbound[i].odoList) {
            // get leg stops
            leg.stops = leg.segments.length < 2 ? 0 : leg.segments.length - 1;
            // get departure and arrival times
            leg.departureTime = moment(
              leg.segments[0].departureDateTime
            ).format("HH:mm");
            leg.arrivalTime = moment(
              leg.segments[leg.segments.length - 1].arrivalDateTime
            ).format("HH:mm");
            let duration = moment.duration(leg.duration);
            let hours = parseInt(duration.asHours());
            let minutes = parseInt(duration.asMinutes()) % 60;
            leg.totalTimeNumber = hours;
            leg.totalTime = `${hours}h${minutes}m`;
            if (state.resultConstants.minTravelTime === 0) {
              state.resultConstants.minTravelTime = leg.duration;
              state.resultConstants.maxTravelTime = leg.duration;
            } else if (leg.duration > state.resultConstants.maxTravelTime) {
              state.resultConstants.maxTravelTime = leg.duration;
            } else if (leg.duration < state.resultConstants.minTravelTime) {
              state.resultConstants.minTravelTime = leg.duration;
            }
            if (outbound[i].biggestTravelTime < leg.duration) {
              outbound[i].biggestTravelTime = leg.duration;
            }
            // determine min and max departure times
            let last = leg.segments.length - 1;
            let outboundTime = leg.segments[0].departureDateTime.split("T")[1];
            let returnTime = leg.segments[last].departureDateTime.split("T")[1];
            if (state.resultConstants.minOutboundTime === 0) {
              state.resultConstants.minOutboundTime = outboundTime;
              state.resultConstants.maxOutboundTime = outboundTime;
            } else if (outboundTime < state.resultConstants.minOutboundTime) {
              state.resultConstants.minOutboundTime = outboundTime;
            } else if (outboundTime > state.resultConstants.maxOutboundTime) {
              state.resultConstants.maxOutboundTime = outboundTime;
            }
            if (state.resultConstants.minReturnTime === 0) {
              state.resultConstants.minReturnTime = returnTime;
              state.resultConstants.maxReturnTime = returnTime;
            } else if (returnTime < state.resultConstants.minReturnTime) {
              state.resultConstants.minReturnTime = returnTime;
            } else if (returnTime > state.resultConstants.maxReturnTime) {
              state.resultConstants.maxReturnTime = returnTime;
            }
            for (let segment of leg.segments) {
              segment.baggage = [];
              searchResults.baggageAllowanceInfos.forEach((info) => {
                if (info.correspondingSegmentIdList.includes(segment.ID)) {
                  segment.baggage.push({
                    name: info.description,
                    type: info.type,
                  });
                  if (
                    info.description.includes("No") ||
                    info.description.includes("+") ||
                    info.description.includes("next")
                  ) {
                    outbound[i].bagWarning = true;
                  }
                }
              });
              if (
                !state.resultConstants.marketingAirlines.includes(
                  segment.airlineCode
                )
              ) {
                state.resultConstants.marketingAirlines.push(
                  segment.airlineCode
                );
              }
              if (
                !outbound[i].marketingAirlines.includes(segment.airlineCode)
              ) {
                outbound[i].marketingAirlines.push(segment.airlineCode);
              }
            }
          }
        }
        state.filteredOutbound = outbound;
        state.outbound = outbound;
        if (!searchResults.isBundled) {
          let inbound = searchResults.inboundItineraries;
          for (let i = 0; i < inbound.length; ++i) {
            inbound[i].key = generateRandomID(3);
            inbound[i].marketingAirlines = [];
            inbound[i].biggestTravelTime = 0;
            inbound[i].absoluteDepartureTime = moment(
              inbound[i].odoList[0].segments[0].departureDateTime
            ).format("HH:mm");
            inbound[i].formattedDepartureDate = moment(
              inbound[i].odoList[0].segments[0].departureDateTime
            ).format("dddd, MMMM Do YYYY");
            inbound[i].absoluteArrivalTime = moment(
              inbound[i].odoList[inbound[i].odoList.length - 1].segments[
                inbound[i].odoList[inbound[i].odoList.length - 1].segments
                  .length - 1
              ].arrivalDateTime
            ).format("HH:mm");
            // // find the price Range
            let currentNumber = Number(inbound[i].amount);
            if (state.resultConstants.minPrice == 0) {
              state.resultConstants.minPrice = currentNumber;
              state.resultConstants.maxPrice = currentNumber;
            } else if (currentNumber < state.resultConstants.minPrice) {
              state.resultConstants.minPrice = currentNumber;
            } else if (currentNumber > state.resultConstants.maxPrice) {
              state.resultConstants.maxPrice = currentNumber;
            }
            for (let leg of inbound[i].odoList) {
              // get leg stops
              leg.stops = leg.segments.length < 2 ? 0 : leg.segments.length - 1;
              // get departure and arrival times
              leg.departureTime = moment(
                leg.segments[0].departureDateTime
              ).format("HH:mm");
              leg.arrivalTime = moment(
                leg.segments[leg.segments.length - 1].arrivalDateTime
              ).format("HH:mm");
              // get total leg time
              let duration = moment.duration(leg.duration);
              let hours = parseInt(duration.asHours());
              let minutes = parseInt(duration.asMinutes()) % 60;
              leg.totalTimeNumber = hours;
              leg.totalTime = `${hours}h${minutes}m`;
              if (state.resultConstants.minTravelTime === 0) {
                state.resultConstants.minTravelTime = leg.duration;
                state.resultConstants.maxTravelTime = leg.duration;
              } else if (hours > state.resultConstants.maxTravelTime) {
                state.resultConstants.maxTravelTime = leg.duration;
              } else if (hours < state.resultConstants.minTravelTime) {
                state.resultConstants.minTravelTime = leg.duration;
              }
              if (inbound[i].biggestTravelTime < leg.duration) {
                inbound[i].biggestTravelTime = leg.duration;
              }
              // determine min and max departure times
              let last = leg.segments.length - 1;
              let outboundTime = leg.segments[0].departureDateTime.split(
                "T"
              )[1];
              let returnTime = leg.segments[last].departureDateTime.split(
                "T"
              )[1];
              if (state.resultConstants.minOutboundTime === 0) {
                state.resultConstants.minOutboundTime = outboundTime;
                state.resultConstants.maxOutboundTime = outboundTime;
              } else if (outboundTime < state.resultConstants.minOutboundTime) {
                state.resultConstants.minOutboundTime = outboundTime;
              } else if (outboundTime > state.resultConstants.maxOutboundTime) {
                state.resultConstants.maxOutboundTime = outboundTime;
              }
              if (state.resultConstants.minReturnTime === 0) {
                state.resultConstants.minReturnTime = returnTime;
                state.resultConstants.maxReturnTime = returnTime;
              } else if (returnTime < state.resultConstants.minReturnTime) {
                state.resultConstants.minReturnTime = returnTime;
              } else if (returnTime > state.resultConstants.maxReturnTime) {
                state.resultConstants.maxReturnTime = returnTime;
              }
              for (let segment of leg.segments) {
                segment.baggage = [];
                searchResults.baggageAllowanceInfos.forEach((info) => {
                  if (info.correspondingSegmentIdList.includes(segment.ID)) {
                    segment.baggage.push({
                      name: info.description,
                      type: info.type,
                    });
                    if (
                      info.description.includes("No") ||
                      info.description.includes("+") ||
                      info.description.includes("next")
                    ) {
                      inbound[i].bagWarning = true;
                    }
                  }
                });
                if (
                  !state.resultConstants.marketingAirlines.includes(
                    segment.airlineCode
                  )
                ) {
                  state.resultConstants.marketingAirlines.push(
                    segment.airlineCode
                  );
                }
                if (
                  !inbound[i].marketingAirlines.includes(segment.airlineCode)
                ) {
                  inbound[i].marketingAirlines.push(segment.airlineCode);
                }
              }
            }
          }
          state.filteredReturn = inbound;
          state.return = inbound;
        }
        state.searchResults = searchResults;
      }
    },
    setOutboundFlight(state, flight) {
      state.selectedFlights.outbound = flight;
      if (state.flags.isDomesticReturn) {
        !isEmpty(state.selectedFlights.return)
          ? (state.flags.allSelected = true)
          : (state.flags.allSelected = false);
      } else {
        state.flags.allSelected = true;
      }
    },
    setReturnFlight(state, flight) {
      state.selectedFlights.return = flight;
      if (!isEmpty(state.selectedFlights.outbound)) {
        state.flags.allSelected = true;
      }
    },
    setPriceResponse(state, response) {
      state.priceResponse = response;
      if (response.products) {
        state.products = response.products.map((product) => {
          return {
            id: product.id,
            selectedValue: true,
          };
        });
      }
    },
    setOutboundFlightMobile(state, flight) {
      state.selectedFlights.outbound = flight;
      if (state.flags.isDomesticReturn) {
        !isEmpty(state.selectedFlights.return)
          ? (state.flags.flightReview = true)
          : (state.flags.flightReview = false);
        !isEmpty(state.selectedFlights.return)
          ? (state.flags.allSelected = true)
          : (state.flags.allSelected = false);
      } else {
        state.flags.flightReview = true;
        state.flags.allSelected = true;
      }
    },
    setReturnFlightMobile(state, flight) {
      state.selectedFlights.return = flight;
      if (!isEmpty(state.selectedFlights.outbound)) {
        state.flags.flightReview = true;
        state.flags.allSelected = true;
      }
    },
    isDomesticReturn(state) {
      state.flags.isDomesticReturn = true;
    },
    notDomesticReturn(state) {
      state.flags.isDomesticReturn = false;
    },
    resetSearchResults(state) {
      state.searchResults = {
        outbound: [],
        return: [],
        errors: [],
      };
      state.allSelected = false;
    },
    resetPriceResponse(state) {
      state.priceResponse = {};
    },
    resetPaymentMethodResponse(state) {
      state.paymentMethodResponse = {};
    },
    resetBookingResponse(state) {
      state.bookingResponse = {};
    },
    updateBaggageCost(state, cost) {
      state.baggageCost = cost;
    },
    updateProducts(state, products) {
      state.products.forEach((product) => {
        if (products.includes(product.id)) {
          product.selectedValue = true;
        } else {
          product.selectedValue = false;
        }
      });
    },
    resetSelectedFlights(state) {
      state.selectedFlights = {
        outbound: {},
        return: {},
      };
      state.flags.allSelected = false;
    },
    resetResultConstants(state) {
      state.resultConstants = {
        marketingAirlines: [],
        minOutboundTime: 0,
        maxOutboundTime: 0,
        minReturnTime: 0,
        maxReturnTime: 0,
        minPrice: 0,
        maxPrice: 0,
        minTravelTime: 0,
        maxTravelTime: 0,
      };
    },
    setPassengers(state, passengers) {
      state.passengers.adults = passengers.adults;
      state.passengers.children = passengers.children;
      state.passengers.infants = passengers.infants;
    },
    setPayer(state, payer) {
      state.payer = payer;
    },
    toggleSearchInProgress(state, progress) {
      state.flags.searchInProgress = progress;
    },
    setReserveResponse(state, reserveResponse) {
      state.reserveResponse = reserveResponse;
    },
    orderResults(state, orderType) {
      let sortPrice = (a, b) => {
        if (b.amount > a.amount) {
          return -1;
        } else if (b.amount < a.amount) {
          return 1;
        } else {
          return 0;
        }
      };
      let sortTime = (a, b) => {
        let aLongestTime = undefined;
        let bLongestTime = undefined;
        for (let leg of a.odoList) {
          if (aLongestTime === undefined) {
            aLongestTime = leg.duration;
          } else if (aLongestTime < leg.duration) {
            aLongestTime = leg.duration;
          }
        }
        for (let leg of b.odoList) {
          if (bLongestTime === undefined) {
            bLongestTime = leg.duration;
          } else if (bLongestTime < leg.duration) {
            bLongestTime = leg.duration;
          }
        }
        if (bLongestTime > aLongestTime) {
          return -1;
        } else if (bLongestTime < aLongestTime) {
          return 1;
        } else {
          return 0;
        }
      };
      if (orderType === "Price") {
        state.filteredOutbound = state.filteredOutbound.sort(sortPrice);
        if (state.flags.isDomesticReturn) {
          state.filteredReturn = state.filteredReturn.sort(sortPrice);
        }
      } else if (orderType === "Time") {
        state.filteredOutbound = state.filteredOutbound.sort(sortTime);
        if (state.flags.isDomesticReturn) {
          state.filteredReturn = state.filteredReturn.sort(sortPrice);
        }
      }
    },
    filterFlights(state, filterOptions) {
      function isDirect(flight) {
        for (let option of flight.odoList) {
          if (option.segments.length > 1) {
            return false;
          }
        }
        return true;
      }
      function customOutboundFilter(flight) {
        if (
          (filterOptions.directOnly && !isDirect(flight)) ||
          Number(flight.amount) < filterOptions.priceRange[0] ||
          Number(flight.amount) > filterOptions.priceRange[1] ||
          flight.biggestTravelTime < filterOptions.travelTime[0] * 60000 ||
          flight.biggestTravelTime > filterOptions.travelTime[1] * 60000 ||
          asNumberTime(flight.absoluteDepartureTime) <
            filterOptions.outboundTime[0] ||
          asNumberTime(flight.absoluteDepartureTime) >
            filterOptions.outboundTime[1] ||
          !flight.marketingAirlines.some(
            (element) => filterOptions.airlines.indexOf(element) >= 0
          )
        ) {
          return false;
        }
        return true;
      }
      function customReturnFilter(flight) {
        if (
          (filterOptions.directOnly && !isDirect(flight)) ||
          Number(flight.amount) < filterOptions.priceRange[0] ||
          Number(flight.amount) > filterOptions.priceRange[1] ||
          flight.biggestTravelTime < filterOptions.travelTime[0] * 60000 ||
          flight.biggestTravelTime > filterOptions.travelTime[1] * 60000 ||
          asNumberTime(flight.absoluteDepartureTime) <
            filterOptions.returnTime[0] ||
          asNumberTime(flight.absoluteDepartureTime) >
            filterOptions.returnTime[1] ||
          !flight.marketingAirlines.some(
            (element) => filterOptions.airlines.indexOf(element) >= 0
          )
        ) {
          return false;
        }
        return true;
      }
      state.filteredOutbound = state.outbound.filter(customOutboundFilter);
      if (state.flags.isDomesticReturn) {
        state.filteredReturn = state.return.filter(customReturnFilter);
      }
    },
  },
  actions: {
    async fetchLocations(context) {
      let countries = await Api.countries();
      context.commit("setCountires", countries);
    },
    resetAll(context) {
      context.commit("resetSelectedFlights");
      context.commit("resetSearchResults");
      context.commit("resetSession");
      context.commit("resetResultConstants");
      context.commit("resetPriceResponse");
      context.commit("resetPaymentMethodResponse");
      context.commit("resetBookingResponse");
    },
  },
});

// returns a random ID of n chunks eg 2; bu4adu2r-uis34yd1
function generateRandomID(n) {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  let randomID = "";
  for (let i = 1; i < n; ++i) {
    randomID += s4() + "-";
  }
  randomID += s4();
  return randomID;
}

function isEmpty(obj) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
}

function asNumberTime(time) {
  return Math.round(moment.duration(time).asHours() * 1e2) / 1e2;
}
